import styled, { css } from 'styled-components';
import newShadow from '~/assets/images/shop/new_shadow.png';
import { Divider } from '../Divider';
import { Text } from '../Text';

const StyledImageBg = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: calc(100% - 2px);
	border-radius: 9px 0px 0px 9px;
`;

const StyledInfoContainer = styled.div`
	padding: 0 15px 0 16px;
	display: flex;
	justify-content: space-between;
`;

const StyledCategory = styled(Text)`
	margin-top: 14px;
	font-size: 12px;
	color: #ffffff;
	opacity: 0.25;
	transition: color 0.3s ease;
`;

const StyledTitle = styled(Text)`
	margin-top: 4px;
	font-weight: 500;
	font-size: 14px;
	color: #ffffff;
	transition: color 0.3s ease;

	span {
		font-weight: 400;
		opacity: 0.5;
	}
`;

const StyledPriceContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 11px;

	img {
		transition: filter 0.3s ease;
	}

	${Text} {
		font-weight: 700;
		font-size: 14px;
		color: #ffffff;
		transition: color 0.3s ease;
	}
`;

const StyledPricesContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const StyledOldPriceContainer = styled(StyledPriceContainer)`
	position: relative;

	img,
	${Text} {
		opacity: 0.25;
	}

	.line {
		position: absolute;
		top: 50%;
		left: -3px;
		transform: translateY(-50%);
		width: calc(100% + 6px);
		height: 1px;
		background-color: #ffffff;
		opacity: 0.5;
		transition: background-color 0.3s ease;
	}
`;

const StyledInfoButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 60px;
	margin-top: 15px;
	background-color: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 5px;
	transition: background-color 0.3s ease;

	img {
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}
`;

const StyledContainer = styled.div<{
	$invertCurrency: boolean;
	$active: boolean;
	$draggable?: boolean;
	$dragHintVisible?: boolean;
}>`
	position: relative;
	display: grid;
	grid-template-columns: 91px 1fr;
	width: 100%;
	height: 90px;
	background-color: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'pointer')};

	${({ $draggable }) =>
		$draggable &&
		css`
			&:active {
				cursor: grabbing;
			}
		`}

	${(props) =>
		props.$active &&
		css`
			background-color: #2d70da;
		`}

	${({ $dragHintVisible, $invertCurrency }) =>
		!$dragHintVisible &&
		css`
			${StyledInfoButton}:hover {
				background-color: #ffffff;

				img {
					filter: invert(1);
				}
			}

			&:hover {
				background-color: #ffffff;

				${StyledImageBg} {
					background-color: #000000;
				}

				${Divider} {
					background-color: #000000;
				}

				${StyledCategory}, ${StyledTitle}, ${StyledPriceContainer} ${Text} {
					color: #000000;
				}

				${StyledInfoButton} {
					background-color: rgba(0, 0, 0, 0.05);
					border: 1px solid rgba(0, 0, 0, 0.1);

					img {
						filter: invert(1);
					}

					&:hover {
						background-color: #000000;

						img {
							filter: invert(0);
						}
					}
				}

				${$invertCurrency
					? css`
							${StyledPriceContainer} img, ${StyledOldPriceContainer} img {
								filter: invert(1);
							}
					  `
					: css`
							${StyledPriceContainer} img, ${StyledOldPriceContainer} img {
								filter: brightness(0);
							}
					  `}

				${StyledOldPriceContainer} {
					.line {
						background-color: #000000;
					}
				}
			}
		`}
`;

const StyledImageContainer = styled.div`
	position: relative;
	width: 90px;
	height: 90px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-right: 1px solid rgba(255, 255, 255, 0.1);

	img {
		user-select: none;
		pointer-events: none;
	}
`;

const StyledNewDot = styled.div`
	position: absolute;
	top: 15px;
	left: 15px;
	z-index: 1;
	width: 8px;
	height: 8px;
	border-radius: 8px;
	background: #eb1818;
`;

const StyledNewShadow = styled.div`
	position: absolute;
	top: 8px;
	left: 8px;
	z-index: 0;
	width: 22px;
	height: 22px;
	background-image: url(${newShadow});
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
`;

const StyledCount = styled.div`
	position: absolute;
	top: 4px;
	left: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 20px;
	padding: 0 5px;
	background-color: #ffffff;
	border-radius: 5px;

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: #000000;
	}
`;

const StyledHintContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	height: 80px;
	padding: 0 10px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 10px;
	pointer-events: none;
	z-index: 9999;
	opacity: 0;
	transition: opacity 0.3s ease;

	&.hint-enter {
		opacity: 0;
	}

	&.hint-enter-active,
	&.hint-enter-done,
	&.hint-exit {
		opacity: 1;
	}

	&.hint-exit-active {
		opacity: 0;
	}
`;

const StyledHintImageContainer = styled.div`
	position: relative;
	width: 60px;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 5px;

	img {
		width: 40px;
		height: 40px;
		user-select: none;
		pointer-events: none;
	}
`;

const StyledHintRightContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledHintInfoContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 9px;
`;

const StyledHintInfoItem = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;

	.icon {
		width: 20px;
		height: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 5px;

		img {
			width: 12px;
			height: 12px;
			user-select: none;
			pointer-events: none;
		}
	}

	${Text} {
		font-weight: 700;
		font-size: 14px;
		color: #000000;

		span {
			font-weight: 400;
			opacity: 0.5;
		}
	}
`;

const StyledDragHintContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: grid;
	grid-template-columns: 20px 1fr 40px;
	align-items: center;
	gap: 20px;
	padding: 0 10px 0 15px;
	background: rgba(17, 19, 20, 0.95);
	border-radius: 10px;
	pointer-events: auto;
	z-index: 9999;
	border: 2px solid #fabe2c;
	opacity: 0;
	transition: opacity 0.3s ease;
	cursor: default;

	&.hint-enter {
		opacity: 0;
	}

	&.hint-enter-active,
	&.hint-enter-done,
	&.hint-exit {
		opacity: 1;
	}

	&.hint-exit-active {
		opacity: 0;
	}
`;

const StyledCrossButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 60px;
	background-color: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 5px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(1);
		}
	}
`;

export {
	StyledCategory,
	StyledContainer,
	StyledCount,
	StyledCrossButton,
	StyledDragHintContainer,
	StyledHintContainer,
	StyledHintImageContainer,
	StyledHintInfoContainer,
	StyledHintInfoItem,
	StyledHintRightContainer,
	StyledImageBg,
	StyledImageContainer,
	StyledInfoButton,
	StyledInfoContainer,
	StyledNewDot,
	StyledNewShadow,
	StyledOldPriceContainer,
	StyledPriceContainer,
	StyledPricesContainer,
	StyledTitle,
};
